import { supabase } from './supabaseClient';
import { isDemoModeEnabled } from './demoMode';
import * as demoData from './demoData';

// Cliente do Worker ca-render-licensing-api (/api/affiliate/*, /api/admin/*).
// Nenhum dado é lido direto do Supabase por aqui — o Worker decide o que
// cada pessoa pode ver, sempre no backend.
//
// DOIS mecanismos de autenticação completamente separados, sem relação um
// com o outro:
//   - Admin: token de sessão do Supabase Auth (e-mail + código OTP) — só
//     usado pelas rotas /api/admin/* e /api/me.
//   - Influenciador: token de sessão OPACO PRÓPRIO (car_asess_...),
//     devolvido por POST /api/affiliate/login (SÓ e-mail, SEM cupom, conta,
//     senha ou cadastro — pedido explícito do proprietário; quem é
//     influenciador vem de uma planilha do Google mantida pelo próprio
//     proprietário, ver worker/src/lib/affiliateSheet.mjs) e guardado só no
//     localStorage deste navegador — nunca passa pelo Supabase Auth.

const WORKER_API_URL = (import.meta.env.VITE_WORKER_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';
const AFFILIATE_TOKEN_STORAGE_KEY = 'ca-render-afiliados-session-token';

export class ApiError extends Error {
  status: number;
  code?: string;

  constructor(status: number, code: string | undefined, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export function getStoredAffiliateToken(): string | null {
  return window.localStorage.getItem(AFFILIATE_TOKEN_STORAGE_KEY);
}

function setStoredAffiliateToken(token: string): void {
  window.localStorage.setItem(AFFILIATE_TOKEN_STORAGE_KEY, token);
}

export function clearStoredAffiliateToken(): void {
  window.localStorage.removeItem(AFFILIATE_TOKEN_STORAGE_KEY);
}

// Chamada crua ao Worker, com ou sem Authorization — usada diretamente só
// por rotas públicas (POST /api/affiliate/login, que não exige nenhuma
// credencial: é o próprio mecanismo de login). Toda rota autenticada usa
// adminFetch/affiliateFetch abaixo, nunca esta função direto.
async function rawFetch(path: string, init: RequestInit, token?: string): Promise<any> {
  const response = await fetch(`${WORKER_API_URL}${path}`, {
    ...init,
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });

  let body: any = null;
  try {
    body = await response.json();
  } catch {
    // resposta sem corpo (ex.: 204) — segue com body = null.
  }

  if (!response.ok || body?.ok === false) {
    const status = body?.status ?? 'unknown_error';
    throw new ApiError(response.status, status, describeApiStatus(status));
  }

  return body;
}

// /api/admin/* e /api/me — token do Supabase Auth.
async function adminFetch(path: string, init: RequestInit = {}): Promise<any> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) {
    throw new ApiError(401, 'no_session', 'Sessão expirada. Entre novamente.');
  }
  return rawFetch(path, init, token);
}

// /api/affiliate/me/* e /api/affiliate/logout — token opaco próprio,
// guardado no localStorage (nunca o Supabase Auth).
async function affiliateFetch(path: string, init: RequestInit = {}): Promise<any> {
  const token = getStoredAffiliateToken();
  if (!token) {
    throw new ApiError(401, 'no_session', 'Sessão expirada. Entre novamente.');
  }
  return rawFetch(path, init, token);
}

// Mensagens amigáveis (pt-BR) para os códigos de status que o Worker
// devolve — mantidas num único lugar para nunca divergirem entre telas.
function describeApiStatus(status: string): string {
  const messages: Record<string, string> = {
    unauthorized: 'Sessão expirada. Entre novamente.',
    forbidden: 'Você não tem permissão para ver isso.',
    not_staff: 'Esta conta não tem acesso ao painel.',
    not_found: 'Não encontramos um influenciador com esses dados.',
    rate_limited: 'Muitas tentativas. Aguarde alguns minutos e tente de novo.',
    method_not_allowed: 'Operação não suportada.',
    invalid_request: 'Dados inválidos. Confira os campos e tente novamente.',
    email_already_registered: 'Já existe uma conta com este e-mail.',
    upstream_timeout: 'O servidor demorou para responder. Tente novamente.',
    upstream_error: 'Erro ao consultar os dados. Tente novamente em instantes.',
    internal_error: 'Erro interno. Tente novamente em instantes.',
  };
  return messages[status] ?? 'Ocorreu um erro inesperado.';
}

// ============================================================================
// Tipos — espelham exatamente o formato devolvido pelo Worker (ver
// worker/src/lib/affiliateSalesService.mjs / affiliateAdminService.mjs).
// ============================================================================

export interface SeriesPoint {
  date: string;
  salesCount: number;
  revenue: number;
  commission: number;
}

// Faixa de comissão vigente NO MÊS ATUAL (pedido explícito do
// proprietário: comissão escalonada por volume de vendas — 1ª-10ª venda
// numa %, 11ª-20ª noutra, 21ª+ noutra, % variando por afiliado, ver
// worker/src/lib/affiliateCommissionTiers.mjs). `null` quando o afiliado
// não tem faixa configurada na planilha (recurso opt-in).
export interface CommissionTierStatus {
  salesCountThisMonth: number;
  tierLabel: string;
  tierPercent: number;
  nextTierLabel: string | null;
  nextTierPercent: number | null;
  salesUntilNextTier: number | null;
}

// Um total monetário por moeda — nunca somado com o de outra moeda (bug
// real em produção, 2026-09-08: uma venda em USD sendo somada com vendas
// em BRL num só número, exibido sempre formatado como Real). Ver
// worker/src/lib/affiliateSalesService.mjs.
export interface CurrencyTotals {
  currency: string;
  salesCount: number;
  revenue: number;
  commission: number;
  netCommission: number;
}

export interface CurrencySeries {
  currency: string;
  series: SeriesPoint[];
}

export interface SaleSummary {
  salesCount: number;
  refundsCount: number;
  byCurrency: CurrencyTotals[];
  seriesByCurrency: CurrencySeries[];
  commissionTierStatus: CommissionTierStatus | null;
}

export interface SaleRow {
  id: string;
  transaction: string;
  product: string | null;
  saleValue: number | null;
  currency: string | null;
  commissionValue: number | null;
  commissionTierLabel: string | null;
  status: string;
  statusLabel: string;
  purchasedAt: string | null;
  approvedAt: string | null;
  refundedAt: string | null;
  canceledAt: string | null;
  chargebackAt: string | null;
}

// hotmartFeeValue/netAfterFeesValue são EXCLUSIVOS do admin (pedido
// explícito do proprietário, 2026-09-08) — nunca aparecem em SaleRow
// (visão do influenciador). `null` significa "não informado" (a Hotmart
// não mandou essa taxa nesse evento) — nunca um valor estimado, o
// frontend mostra 'N/D' nesse caso (ver formatCurrency).
export interface AdminSaleRow extends SaleRow {
  affiliateName: string | null;
  affiliateEmail: string | null;
  hotmartFeeValue: number | null;
  netAfterFeesValue: number | null;
}

export interface SaleListResult<T> {
  rows: T[];
  page: number;
  pageSize: number;
  hasMore: boolean;
}

// Espelha a planilha do Google (ver worker/src/lib/affiliateSheet.mjs) —
// não existe mais cadastro/edição por aqui, só leitura. Sem id/active/
// datas: a planilha não tem esses conceitos, é editada diretamente pelo
// proprietário fora deste painel.
export interface Affiliate {
  name: string;
  email: string;
  couponCodes: string[];
  // Chave Pix da coluna nova da planilha (2026-09-16) — texto livre, só
  // pra o admin ver pra onde pagar. `null` se a planilha ainda não tiver
  // essa coluna, ou a célula estiver vazia pra esse afiliado.
  pixKey: string | null;
}

// Mesmo critério de CurrencyTotals acima, com netAfterFees no lugar de
// netCommission (exclusivo do admin — soma venda - taxa da Hotmart só das
// vendas daquela moeda cuja taxa é conhecida; `null` se nenhuma vier
// informada, nunca 0).
export interface AdminCurrencyTotals {
  currency: string;
  salesCount: number;
  revenue: number;
  commission: number;
  netAfterFees: number | null;
  // Quantas vendas de salesCount entraram na soma de netAfterFees (só as
  // que têm hotmart_fee_value conhecido — ex.: venda importada de um CSV
  // de histórico nunca tem). Quando < salesCount, netAfterFees é PARCIAL —
  // o frontend precisa avisar disso, nunca mostrar como se fosse o total
  // (achado real, 2026-09-09: ficava parecendo "conta errada").
  netAfterFeesKnownCount: number;
}

export interface AdminRankingCurrencyTotals {
  currency: string;
  salesCount: number;
  revenue: number;
  commission: number;
}

export interface AdminOverview {
  totals: {
    salesCount: number;
    approvedCount: number;
    completeCount: number;
    refundsCount: number;
    chargebacksCount: number;
    byCurrency: AdminCurrencyTotals[];
  };
  ranking: Array<{
    name: string;
    email: string | null;
    refundsCount: number;
    byCurrency: AdminRankingCurrencyTotals[];
  }>;
  // Tráfego pago / rastreamento de origem (2026-09-30) — independente do
  // ranking de afiliados acima: agrupa venda por data.purchase.origin.
  // {src,sck,xcod} da Hotmart (ver worker/src/lib/hotmartSaleMapping.mjs),
  // usado quando o anúncio linka direto pra Hotmart, sem cupom nenhum.
  // Array vazio quando nenhuma venda do período tem essa informação.
  bySource: Array<{
    sourceCode: string;
    byCurrency: AdminRankingCurrencyTotals[];
  }>;
  seriesByCurrency: CurrencySeries[];
}

export interface SaleListFilters {
  page?: number;
  pageSize?: number;
  status?: string;
  from?: string;
  to?: string;
  search?: string;
  sortDirection?: 'asc' | 'desc';
}

function buildQuery(params: object): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params as Record<string, unknown>)) {
    if (value !== undefined && value !== '') {
      search.set(key, String(value));
    }
  }
  const query = search.toString();
  return query.length > 0 ? `?${query}` : '';
}

// ----------------------------------------------------------------------------
// Admin — identidade (confirma que a conta logada no Supabase Auth tem
// acesso administrativo ativo).
// ----------------------------------------------------------------------------

export async function fetchAdminMe(): Promise<void> {
  if (isDemoModeEnabled()) return;
  await adminFetch('/api/me');
}

// ----------------------------------------------------------------------------
// Login do influenciador — SEM conta, senha ou cadastro. Só e-mail,
// verificado contra a planilha do Google (ver
// worker/src/lib/affiliateLoginService.mjs). Guarda o token retornado no
// localStorage — chamado só por AffiliateSessionContext.
// ----------------------------------------------------------------------------

export interface AffiliateLoginResult {
  affiliateName: string;
  expiresAt: string;
}

export async function loginAffiliate(email: string): Promise<AffiliateLoginResult> {
  const body = await rawFetch('/api/affiliate/login', { method: 'POST', body: JSON.stringify({ email }) });
  setStoredAffiliateToken(body.sessionToken);
  return { affiliateName: body.affiliateName, expiresAt: body.expiresAt };
}

// "Sair" nunca pode travar por causa de um erro de rede — sempre limpa a
// sessão local e volta pra tela de consulta, mesmo que a chamada ao
// Worker (revogação server-side, best-effort) falhe.
export async function logoutAffiliate(): Promise<void> {
  const token = getStoredAffiliateToken();
  clearStoredAffiliateToken();
  if (token) {
    await rawFetch('/api/affiliate/logout', { method: 'POST' }, token).catch(() => {});
  }
}

// ----------------------------------------------------------------------------
// Painel do influenciador
// ----------------------------------------------------------------------------

export async function fetchMySummary(
  range: { from?: string; to?: string }
): Promise<{ summary: SaleSummary; affiliateName: string; couponCodes: string[] }> {
  if (isDemoModeEnabled()) return demoData.getDemoMySummary();
  const body = (await affiliateFetch(`/api/affiliate/me/summary${buildQuery(range)}`)) as {
    summary: SaleSummary;
    affiliateName: string;
    couponCodes: string[];
  };
  return body;
}

export async function fetchMySales(filters: SaleListFilters): Promise<SaleListResult<SaleRow>> {
  if (isDemoModeEnabled()) return demoData.getDemoMySales();
  const body = (await affiliateFetch(`/api/affiliate/me/sales${buildQuery(filters)}`)) as SaleListResult<SaleRow> & { ok: true };
  return body;
}

export interface AffiliatePayment {
  id: string;
  amount: number;
  currency: string;
  paidAt: string;
  note: string | null;
}

export interface AffiliatePaymentsCurrencyTotals {
  currency: string;
  commission: number;
  paid: number;
  pending: number;
}

export interface AffiliatePaymentsSummary {
  byCurrency: AffiliatePaymentsCurrencyTotals[];
  payments: AffiliatePayment[];
}

// "A receber" (pending) / "Pago" (paid) do próprio afiliado logado, mais o
// histórico de pagamentos — pra aba "Pagamentos" do dashboard dele.
export async function fetchMyPayments(): Promise<AffiliatePaymentsSummary> {
  if (isDemoModeEnabled()) return demoData.getDemoMyPayments();
  const body = (await affiliateFetch('/api/affiliate/me/payments')) as AffiliatePaymentsSummary & { ok: true };
  return body;
}

// ----------------------------------------------------------------------------
// Área administrativa
// ----------------------------------------------------------------------------

export async function fetchAdminOverview(range: { from?: string; to?: string }): Promise<AdminOverview> {
  if (isDemoModeEnabled()) return demoData.getDemoAdminOverview();
  const body = (await adminFetch(`/api/admin/overview${buildQuery(range)}`)) as AdminOverview & { ok: true };
  return body;
}

export async function fetchAdminSales(filters: SaleListFilters & { affiliateEmail?: string }): Promise<SaleListResult<AdminSaleRow>> {
  if (isDemoModeEnabled()) return demoData.getDemoAdminSales();
  const body = (await adminFetch(`/api/admin/sales${buildQuery(filters)}`)) as SaleListResult<AdminSaleRow> & { ok: true };
  return body;
}

// Só leitura — reflete a planilha do Google (ver worker/src/lib/affiliateSheet.mjs).
// Não existe mais cadastro/edição de afiliado pelo painel: a planilha é
// editada diretamente pelo proprietário, fora deste sistema.
export async function fetchAdminAffiliates(): Promise<Affiliate[]> {
  if (isDemoModeEnabled()) return demoData.getDemoAffiliates();
  const body = (await adminFetch('/api/admin/affiliates')) as { affiliates: Affiliate[] };
  return body.affiliates;
}

export interface SyncResult {
  configured: boolean;
  from?: string;
  to?: string;
  processed?: number;
  created?: number;
  updated?: number;
  truncated?: boolean;
}

// POST /api/admin/sync — reconciliação com a Sales API da Hotmart (cobre
// Webhook perdido). configured:false não é um erro — significa que o
// admin ainda não cadastrou HOTMART_CLIENT_ID/SECRET/BASIC_TOKEN no
// Worker (ver ../../docs/PAINEL_AFILIADOS.md).
export async function syncAdminSales(range: { from?: string; to?: string } = {}): Promise<SyncResult> {
  if (isDemoModeEnabled()) return demoData.getDemoSyncResult();
  const body = (await adminFetch('/api/admin/sync', { method: 'POST', body: JSON.stringify(range) })) as SyncResult & { ok: true };
  return body;
}

// "A receber"/"Pago" + histórico de UM afiliado escolhido pelo admin (pra
// tela onde ele registra um pagamento novo).
export async function fetchAdminAffiliatePayments(affiliateEmail: string): Promise<AffiliatePaymentsSummary> {
  if (isDemoModeEnabled()) return demoData.getDemoAdminAffiliatePayments(affiliateEmail);
  const body = (await adminFetch(`/api/admin/affiliate-payments${buildQuery({ affiliateEmail })}`)) as AffiliatePaymentsSummary & {
    ok: true;
  };
  return body;
}

// Registra um pagamento novo pra um afiliado — nunca calcula nada sozinho,
// só grava o valor que o admin informou.
export async function recordAdminAffiliatePayment(input: {
  affiliateEmail: string;
  amount: number;
  currency: string;
  paidAt: string;
  note?: string;
}): Promise<AffiliatePayment> {
  const body = (await adminFetch('/api/admin/affiliate-payments', {
    method: 'POST',
    body: JSON.stringify(input),
  })) as { payment: AffiliatePayment };
  return body.payment;
}

// Cria uma NOVA conta de admin (Supabase Auth + staff_users) — nunca usado
// pra influenciador, que não tem conta nenhuma.
export async function createAdminAccount(email: string): Promise<{ userId: string }> {
  const body = (await adminFetch('/api/admin/staff-accounts', {
    method: 'POST',
    body: JSON.stringify({ email }),
  })) as { userId: string };
  return body;
}
